import { UserData, UserType } from '../../../../types';
import { gracefullyParseJson } from '../../../../utils';
import { ACTIVE_SPEAKER_THRESHOLD } from './constants';
import { MediaSource } from './media/types';
import type {
  RemoteAudioStatsByTrackSid,
  RemoteMicrophoneByParticipantId,
  RemoteParticipantMediaByParticipantId,
} from './types';

function decodeMetadata(str?: string) {
  if (!str) return;
  // backend does a urlsafe base64 encoding
  // atob will do a base64 decode, but we also need to replace the urlsafe part '-' and '_' by hand
  try {
    return atob(str.replace(/-/g, '+').replace(/_/g, '/'));
  } catch {
    // preserve legacy behavior
    return str.replace(/~~/g, '/');
  }
}

export function parseIdentity(identity: string | null | undefined): UserData {
  const [strUserType, id, rawMetadata] = identity?.split('|') ?? [];
  const metadata = decodeMetadata(rawMetadata);

  const userType = strUserType as UserType;

  return { userType, id, ...gracefullyParseJson(metadata) };
}

/**
 * Parses the entity ID for patient users to extract the patient short ID.
 * Please use this sparingly and preferably only in non-vital functionality e.g.
 * Segment events as this is an implementation detail of how we encode
 * participant identities.
 */
export function entityIdToPatientShortId(entityId: string) {
  return entityId.split('_')[0];
}

// Used to get active speakers
export function getRemoteParticipantMicrophone(
  remoteMediaByParticipantId: RemoteParticipantMediaByParticipantId,
  participantId: string,
) {
  const remoteAudio = remoteMediaByParticipantId[participantId]?.audio;
  if (!remoteAudio) return;
  const [remoteMicrophone] = remoteAudio.filter(
    (remoteAudioDelegator) => remoteAudioDelegator.source === MediaSource.Microphone,
  );
  return remoteMicrophone;
}

export function filterActiveParticipantIds(
  remoteMicrophoneTracksByParticipantId: RemoteMicrophoneByParticipantId,
  remoteTrackStatsBySid: RemoteAudioStatsByTrackSid,
) {
  return Object.entries(remoteMicrophoneTracksByParticipantId).reduce((acc, [participantId, remoteMicrophoneTrack]) => {
    const remoteAudioTrackStats = remoteTrackStatsBySid[remoteMicrophoneTrack.sid];
    if (!remoteAudioTrackStats) return acc;
    const { audioLevel } = remoteAudioTrackStats;
    if ((audioLevel ?? 0) < ACTIVE_SPEAKER_THRESHOLD || !remoteMicrophoneTrack.isEnabled) return acc;
    return acc.add(participantId);
  }, new Set<string>());
}
